/*
 * Copyright (c) 2022. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import type { TypographyProps } from '@mui/material';
import {
  AuxPower,
  type BidMatrixScenario,
  BidRequestInputApprovalStatus,
  type BidRequestProcurementInputRecord,
  type BidRequestProcurementInputs,
  BidRequestStage,
  type ESSCostingConfiguration,
  type FIMOConfiguration,
  NetworkUpgradeTiming,
  PlantConfiguration,
  type ProjectSpecificData,
  type PVCostingConfiguration,
  type PVSimConfiguration,
  ValidDepreciationValues,
} from '@poweralpha/common';
import type { DeepKeys } from '@tanstack/react-form';
import { add } from 'date-fns';
import dayjs from 'dayjs';
import { invert, mean, min, omit, sum, uniqueId } from 'lodash';
import type { SimulateStateParams as ESSSimulateStateParams } from '../ess/models';
import type { SimulateForm as ESSSimulateForm } from '../ess/schemas';
import type { SimulateStateParams as FIMOSimulateStateParams } from '../fimo/models';
import type { SimulateStateParams as PVSimulateStateParams } from '../pv/pvcosting/models';
import type { SimulateStateParams as PVSimSimulateStateParams } from '../pv/pvsim/models';
import {
  type ConfigOverrideMap,
  createConfigOverrideMap,
} from '../react-components/configs/overrides';
import { getEndOfMonthUTC } from '../utils';
import type { NotificationForm } from './models';
import {
  type AddConfigsForm,
  type BidRequestScenarioForm,
  type CoreLandFields,
  type EditBidMatrixForm2,
  type EditDevForm,
  type EditOrigBidRequestForm,
  type EditTxmForm,
  getConfigEntrySchema,
  type IcrForm,
  type NewBidRequestForm,
  type ReviewBidRequestForm,
} from './schemas';

const now = new Date();

export const DEFAULT_NOTIF_FORM: NotificationForm = {
  comment: '',
  notifications: {
    notifs: [],
    others: [],
  },
};

export const defaultIcrFormValues: IcrForm = {
  icr: null,
  id: '',
  name: '',
  nu_payment_timing: NetworkUpgradeTiming.AT_COD,
  nu_payment_timing_other_comments: '',
  poi_mw_ac: '',
  reimb_nu_costs: '',
  nonreimb_nu_costs: '',
  refund_period_months: '',
  refund_interest_rate: '',
  ic_voltage_kv: '',
  lc_posting_date: now,
  lc_posting_cost: '',
  aux_power: AuxPower.RETAIL,
  aux_power_comment: '',
};

export const defaultCoreLandValues: CoreLandFields = {
  lease_cost: '0',
  purchase_cost: '0',
  purchase_acres_under_control: '',
  lease_acres_under_control: '',
  owned_in_fee_acres: '',
  owned_in_fee_cost: '0',
  lease_escalation: '',
};

export const defaultDiscretionaryCost: EditDevForm['budget']['discretionary_costs'][number] = {
  name: '',
  date: now,
  value: '0',
};

export const DEFAULT_OPPTY_DATA: EditOrigBidRequestForm['opportunity_data'] = {
  transaction: 'PPA',
  ppa_security_pre_cod: '',
  ppa_security_post_cod: '',
  offtake_bid_fee: '',
  offtake_success_broker_fee: '',
  ppa_security_pre_cod_start_date: null,
};

export const BID_MATRIX_FORM_DEFAULTS: EditBidMatrixForm2 = {
  plant_types: [],
  bid_matrix: [],
  deleted_scenarios: [],
};

export const EDIT_ORIG_BID_REQUEST_DEFAULTS: EditOrigBidRequestForm = {
  opportunity_data: DEFAULT_OPPTY_DATA,
  comment: '',
  probability_of_exceedance: 'P50',
};

export const NEW_BID_REQUEST_DEFAULTS: NewBidRequestForm = {
  ...EDIT_ORIG_BID_REQUEST_DEFAULTS,
  version: 1,
  name: '',
  stage: BidRequestStage.DRAFT,
  notifs: [],
  others: [],
};

export const addConfigsDefaultValues: AddConfigsForm = {
  scenarios: [],
};

export const DEFAULT_INPUTS_APPROVALS_FORM_VALUES: ReviewBidRequestForm = {
  approvals: [],
  status: BidRequestInputApprovalStatus.NOT_REVIEWED,
  peLevel: 'P50',
  comments: '',
  notifications: {
    notifs: [],
    others: [],
  },
};

export const defaultNewScenario: BidRequestScenarioForm = {
  id: null,
  index: 0,
  applicable_icrs: [],
  cod: new Date(),
  contracted_term: '',
  delivery_point: 'Bus Bar',
  delivery_point_hub: '',
  ess_capacity: '',
  ess_capacity_payment: '',
  ess_capacity_payment_esc: '',
  ess_cycles: '',
  ess_maintain_capacity: false,
  ess_power: '',
  ess_term: '',
  notes: '',
  plant_type: '',
  poi_mw_ac: '',
  products: [],
  project: null,
  project_id: '',
  pv_ac_capacity: '',
  pv_ppa_escalation: '',
  pv_ppa_price: '',
  wind_capacity: '',
  wind_ppa_price: '',
  wtsf: '',
  edited: false,
  ess_availability_guarantee_structure: 'not_required',
  ess_has_specific_offtake_power_factor: false,
};

// for typing only
export const addConfigDefaultValues: AddConfigsForm['scenarios'][0] = {
  ...defaultNewScenario,
  bid_matrix_id: 0,
  pv: getConfigEntrySchema<PVCostingConfiguration['request']>().parse({}),
  pvsim: getConfigEntrySchema<PVSimConfiguration['request']>().parse({}),
  ess: getConfigEntrySchema<ESSCostingConfiguration['request']>().parse({}),
  fimo: getConfigEntrySchema<FIMOConfiguration['request']>().parse({}),
  procurement_inputs: null,
};

export const defaultTxmFormValues: EditTxmForm = {
  project_id: null,
  project_txm_data: {
    icr_ids: [],
    curtailment: '1',
    merchant: { prices: null },
    contract_period_wheeling_first_year: '',
    merchant_period_wheeling_first_year: '',
    txm_comment: '',
  },
  bid_matrix: [],
  icr_data: [],
  files: {
    supplementary_xlsx_files: [],
    previously_uploaded_xlsx_files: [],
  },
};

export const defaultDevFormValues: EditDevForm = {
  land: {
    ...defaultCoreLandValues,
    coordinates_lat: '35',
    coordinates_long: '-120',
    miles_to_ic: '',
    min_lease_acres: '',
    min_purchase_acres: '',
    site_perc_timber: '0',
    total_easement_costs: '',
    land_comment: '',
    selected_sites: {
      default: { purchase: [], lease: [], owned_in_fee: [], custom: null },
      per_scenario: {},
    },
  },
  budget: {
    is_devex_por: true,
    devex_budget: '',
    energy_communities_bonus: false,
    budget_comment: '',
    discretionary_costs: [],
  },
  files: {
    supplementary_xlsx_files: [],
    previously_uploaded_xlsx_files: [],
  },
  bid_matrix: [],
};

// order matters!!
export const orderedMetricLabelMapping: Partial<Record<keyof BidRequestScenarioForm, string>> = {
  products: 'Products',
  project: 'Project',
  plant_type: 'Plant Type',
  wtsf: 'WTSF',
  cod: 'COD',
  contracted_term: 'Term (years)',
  delivery_point: 'Delivery Point',
  delivery_point_hub: 'Delivery Point Hub',
  poi_mw_ac: 'Customer Desired POI (MW-ac)',
  pv_ac_capacity: 'PV AC Capacity (MW-ac)',
  pv_ppa_price: 'Target PV PPA Price ($/MWh)',
  pv_ppa_escalation: 'PV PPA Escalation (%)',
  ess_capacity: 'ESS Capacity (MWh-ac)',
  ess_power: 'ESS Power (MW-ac)',
  ess_term: 'ESS Term (years)',
  ess_cycles: 'ESS Cycles (#)',
  ess_maintain_capacity: 'Maintain Capacity?',
  ess_capacity_payment: 'Target Capacity Payment ($/MW/month)',
  ess_capacity_payment_esc: 'ESS Capacity Payment Esc (%)',
  ess_availability_guarantee_structure: 'ESS Availability Guarantee Structure',
  ess_availability_guarantee_perc: 'ESS Availability Guarantee (%)',
  ess_specific_offtake_power_factor: 'ESS Specific Offtake Power Factor',
  wind_capacity: 'Wind Capacity (MW-ac)',
  wind_ppa_price: 'Wind PPA Price ($/MWh)',
  notes: 'Notes',
};

export const customMetricNameMapping = invert(orderedMetricLabelMapping) as Record<
  string,
  keyof BidMatrixScenario
>;

export const getPvsimOverrides = (
  projectValues: ProjectSpecificData | null,
  scenarioValues: BidMatrixScenario | undefined,
): PVSimSimulateStateParams => ({
  poi_mw_ac: scenarioValues?.poi_mw_ac,
  simulation_start: add(getEndOfMonthUTC(scenarioValues?.cod), { days: 1 })?.toISOString(),
  lat_lng: projectValues?.land?.coordinates,
});

const hasMptValues = (mpts: BidRequestProcurementInputs['mpts'] | undefined) =>
  Boolean(mpts?.some((mpt) => mpt.cost_per_count != null || mpt.mva_capacity != null));

const hasCompleteMpt = (mpts: BidRequestProcurementInputs['mpts'] | undefined) =>
  Boolean(mpts?.some((mpt) => mpt.cost_per_count != null && mpt.mva_capacity != null));

// A procurement row is complete (gates procurement approval) when: PV scenarios have
// a PV module cost, HV breakers cost is set, and at least one MPT has both cost and
// capacity.
export const isProcurementInputComplete = (
  plantType: string,
  inputs: BidRequestProcurementInputs | null | undefined,
) => {
  if (!inputs) return false;
  const hasPvModulesInput = !plantType.includes('PV') || inputs.pv_modules_cost_per_kw_dc !== null;
  return (
    hasPvModulesInput && inputs.hv_breakers_cost_per_count !== null && hasCompleteMpt(inputs.mpts)
  );
};

export const hasProcurementInputValues = (inputs: BidRequestProcurementInputs | null | undefined) =>
  Boolean(
    inputs &&
      (inputs.pv_modules_cost_per_kw_dc != null ||
        inputs.hv_breakers_cost_per_count != null ||
        (inputs.other_equipment_notes != null && inputs.other_equipment_notes !== '') ||
        hasMptValues(inputs.mpts)),
  );

export const hasEssProcurementInputValues = (
  inputs: BidRequestProcurementInputs | null | undefined,
) => Boolean(inputs && (inputs.hv_breakers_cost_per_count != null || hasMptValues(inputs.mpts)));

export const getProcurementInputsCodKey = (cod: Date | string | null | undefined) => {
  if (!cod) return '';
  // Compute the month end in UTC so a COD stored as UTC midnight on the 1st
  // does not roll back to the previous month in negative-offset timezones.
  const codDayjs = dayjs(cod).utc(false);
  return codDayjs.isValid() ? codDayjs.endOf('month').format('YYYY-MM-DD') : '';
};

export const getProcurementInputsForProjectCod = (
  procurementInputs: Array<BidRequestProcurementInputRecord> | undefined,
  projectId: string | null | undefined,
  cod: Date | string | null | undefined,
): BidRequestProcurementInputs | null => {
  const codKey = getProcurementInputsCodKey(cod);
  if (!codKey || !projectId) return null;

  return (
    procurementInputs?.find(
      (entry) => entry.project_id === projectId && getProcurementInputsCodKey(entry.cod) === codKey,
    )?.data ?? null
  );
};

export const getPvOverrides = (
  projectValues: ProjectSpecificData | null,
  scenarioValues:
    | (BidMatrixScenario & { procurement_inputs?: BidRequestProcurementInputs | null })
    | undefined,
  icrKv: number | undefined | null,
  pvsimConfig?: PVSimConfiguration,
): PVSimulateStateParams => {
  const hasProcurementInputs = hasProcurementInputValues(scenarioValues?.procurement_inputs);

  return {
    pvsim_config: hasProcurementInputs ? undefined : pvsimConfig,
    cod: getEndOfMonthUTC(scenarioValues?.cod)?.toISOString(),
    miles_to_ic: projectValues?.land?.miles_to_ic ?? undefined,
    ic_voltage_kv: icrKv ?? undefined,
    site_perc_timber: projectValues?.land?.site_perc_timber ?? undefined,
    land_purchase_cost: projectValues?.land?.purchase_cost ?? undefined,
    land_lease_cost: projectValues?.land?.lease_cost ?? undefined,
    term: scenarioValues?.contracted_term ?? undefined,
    procurement_inputs: hasProcurementInputs ? scenarioValues?.procurement_inputs : undefined,
  };
};

export const getEssOverrides = (
  projectValues: ProjectSpecificData | null,
  scenarioValues:
    | (BidMatrixScenario & { procurement_inputs?: BidRequestProcurementInputs | null })
    | undefined,
  icrKv: number | undefined | null,
  pvsimConfig?: PVSimConfiguration,
): ESSSimulateStateParams => {
  const hasPv = scenarioValues?.plant_type.includes(PlantConfiguration.PV) ?? false;
  const hasProcurementInputs = hasEssProcurementInputValues(scenarioValues?.procurement_inputs);
  return {
    poi_mw_ac: scenarioValues?.poi_mw_ac,
    cod: getEndOfMonthUTC(scenarioValues?.cod)?.toISOString(),
    power: scenarioValues?.ess_power ?? undefined,
    capacity: scenarioValues?.ess_capacity ?? undefined,
    maintain_capacity: scenarioValues?.ess_maintain_capacity,
    cycles: scenarioValues?.ess_cycles ?? undefined,
    incl_hv: !hasPv,
    miles_to_ic: projectValues?.land?.miles_to_ic ?? undefined,
    ic_voltage_kv: icrKv ?? undefined,
    term: scenarioValues?.ess_term ?? scenarioValues?.contracted_term ?? undefined,
    purchase_cost_per_acre: projectValues?.land?.purchase_cost ?? undefined,
    annual_lease_rate: projectValues?.land?.lease_cost ?? undefined,
    txm_loss: pvsimConfig?.request.losses?.transmission_line ?? undefined,
    mpt_loss:
      pvsimConfig?.request.losses?.hv_transformer?.total_loss_full_load_percent ?? undefined,
    reactive_mpt_loss:
      pvsimConfig?.request.losses?.hv_transformer?.xr_short_circuit_voltage_percent ?? undefined,
    procurement_inputs:
      !hasPv && hasProcurementInputs ? scenarioValues?.procurement_inputs : undefined,
  };
};

const essOverrideSources = {
  poi_mw_ac: 'Bid Request',
  cod: 'Bid Request',
  power: 'Bid Request',
  capacity: 'Bid Request',
  maintain_capacity: 'Bid Request',
  cycles: 'Bid Request',
  incl_hv: 'Bid Request',
  miles_to_ic: 'Bid Request',
  ic_voltage_kv: 'Bid Request',
  term: 'Bid Request',
  purchase_cost_per_acre: 'Bid Request',
  annual_lease_rate: 'Bid Request',
  procurement_inputs: 'Bid Request',
  txm_loss: 'PVSim',
  mpt_loss: 'PVSim',
  reactive_mpt_loss: 'PVSim',
} satisfies Partial<Record<keyof ESSSimulateStateParams & string, 'Bid Request' | 'PVSim'>>;

const essOverridePaths = {
  poi_mw_ac: 'hv_costs.interconnection_size',
  cod: 'project.cod',
  power: 'project.ac_power_rating',
  capacity: 'project.ac_energy_rating',
  maintain_capacity: 'augmentation.capacity_maintenance',
  cycles: 'components.ess_op_envelope.cycles_per_year',
  incl_hv: 'hv_costs.incl_hv',
  miles_to_ic: 'hv_costs.miles_to_ic',
  ic_voltage_kv: 'hv_costs.interconnection_voltage',
  term: 'project.contracted_term',
  purchase_cost_per_acre: 'costs.purchase_cost_per_acre',
  annual_lease_rate: 'costs.annual_lease_rate',
  procurement_inputs: 'boms.main',
  txm_loss: 'losses.txm_loss',
  mpt_loss: 'losses.main_power_transformer',
  reactive_mpt_loss: 'losses.x_trans_hv',
} satisfies Partial<Record<keyof ESSSimulateStateParams & string, DeepKeys<ESSSimulateForm>>>;

export const getEssOverrideMap = (
  projectValues: ProjectSpecificData | null,
  scenarioValues:
    | (BidMatrixScenario & { procurement_inputs?: BidRequestProcurementInputs | null })
    | undefined,
  icrKv: number | undefined | null,
  pvsimConfig?: PVSimConfiguration,
): ConfigOverrideMap<ESSSimulateStateParams, DeepKeys<ESSSimulateForm>> | undefined =>
  createConfigOverrideMap<ESSSimulateStateParams, DeepKeys<ESSSimulateForm>>(
    getEssOverrides(projectValues, scenarioValues, icrKv, pvsimConfig),
    {
      paths: essOverridePaths,
      sources: essOverrideSources,
    },
  );

export const getFimoOverrides = (
  projectValues: ProjectSpecificData | null,
  scenarioValues: BidMatrixScenario | undefined,
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: '??' causing complexity here
): FIMOSimulateStateParams => {
  const icrs = Object.entries(projectValues?.txm?.icrs ?? {})
    .filter(([id]) => scenarioValues?.applicable_icrs.includes(id))
    .map(([, data]) => data);
  const lgiaPostingDates = icrs.map(({ lc_posting_date }) =>
    lc_posting_date ? new Date(lc_posting_date) : new Date(),
  );
  const non_reimb_network_upgrade_cost = sum(
    icrs.map(({ nonreimb_nu_costs }) => nonreimb_nu_costs),
  );
  const reimb_network_upgrade_cost = sum(icrs.map(({ reimb_nu_costs }) => reimb_nu_costs));
  const plant_configuration = (scenarioValues?.plant_type.split('+') ?? [
    PlantConfiguration.PV,
  ]) as Array<PlantConfiguration>;

  const refundInterestRate = mean(icrs.map((icrData) => icrData.refund_interest_rate));
  const refundInterestPeriod = mean(icrs.map((icrData) => icrData.refund_period_months));

  const cod = getEndOfMonthUTC(scenarioValues?.cod);

  return {
    plant_configuration,
    cod: cod?.toISOString(),
    pv_contracted_term: plant_configuration.includes(PlantConfiguration.PV)
      ? (scenarioValues?.contracted_term ?? 0)
      : 0,
    ess_contracted_term: plant_configuration.includes(PlantConfiguration.BESS)
      ? (scenarioValues?.ess_term ?? 0)
      : 0,
    lc_lgia_posting_date: min(lgiaPostingDates)?.toISOString(),
    network_upgrade_reimbursement_period: Number.isNaN(refundInterestPeriod)
      ? undefined
      : refundInterestPeriod,
    network_upgrade_reimbursement_interest_rate: Number.isNaN(refundInterestRate)
      ? undefined
      : refundInterestRate,
    non_reimb_network_upgrade_cost,
    reimb_network_upgrade_cost,
    lc_lgia_size: non_reimb_network_upgrade_cost + reimb_network_upgrade_cost,
    lc_amount: sum(icrs.map((icrData) => icrData.lc_posting_cost)),
    nu_payment_timing: icrs?.at(0)?.nu_payment_timing,
    contract_wheeling_charge_first_yr:
      projectValues?.txm?.contract_period_wheeling_first_year ?? undefined,
    merchant_wheeling_charge_first_yr:
      projectValues?.txm?.merchant_period_wheeling_first_year ?? undefined,
    purchase_cost_per_acre: projectValues?.land?.purchase_cost ?? undefined,
    annual_lease_rate: projectValues?.land?.lease_cost ?? undefined,
    land_lease_escalation: projectValues?.land?.lease_escalation ?? undefined,
    lease_acreage: projectValues?.land?.lease_acres_under_control ?? undefined,
    purchase_acreage: projectValues?.land?.purchase_acres_under_control ?? undefined,
    devex_budget: projectValues?.dev?.devex_budget ?? undefined,
    pv_ppa_price: scenarioValues?.pv_ppa_price ?? undefined,
    pv_ppa_escalation: scenarioValues?.pv_ppa_escalation ?? undefined,
    ess_capacity_payment: scenarioValues?.ess_capacity_payment ?? undefined,
    ess_capacity_payment_esc: scenarioValues?.ess_capacity_payment_esc ?? undefined,
    merchant_prices_id: projectValues?.txm?.merchant_prices_id,
    curtailment: projectValues?.txm?.curtailment ?? undefined,
    one_time_post_cod_costs:
      projectValues?.dev?.discretionary_costs
        ?.filter((cost) => dayjs(cost.date).isAfter(cod))
        .map((cost) => ({
          ...omit(cost, 'name'),
          id: uniqueId('one_time_post_cod_costs'),
          value: cost.value.toString(),
          description: cost.name,
          depreciation_schedule: ValidDepreciationValues.NOT_APPLICABLE,
        })) ?? undefined,
    one_time_pre_cod_costs:
      projectValues?.dev?.discretionary_costs
        ?.filter((cost) => dayjs(cost.date).isBefore(cod))
        .map((cost) => ({
          ...omit(cost, 'name'),
          id: uniqueId('one_time_pre_cod_costs'),
          value: cost.value.toString(),
          description: cost.name,
          depreciation_schedule: ValidDepreciationValues.INDIRECT,
        })) ?? undefined,
  };
};

export const defaultFinanceFiles: { xlsx: Array<File>; pdf: Array<File> } = {
  xlsx: [],
  pdf: [],
};

export const defaultValueTypographyProps: TypographyProps = {
  variant: 'h6',
  sx: {
    fontSize: 16,
    lineHeight: 1,
  },
};

export const textAreaTypographyProps: TypographyProps = {
  noWrap: false,
  sx: {
    fontSize: 12,
    lineHeight: 1,
    fontWeight: 'fontWeightBold',
  },
};
